// src/services/employeeService.js
import apiClient from './api/client';

const BASE_URL = '/api/employees';

/**
 * Employee Service
 * Handles comprehensive employee management operations
 */
export const employeeService = {
  // === Employee Details ===
  getEmployeeDetails: async (userId) => {
    try {
      const response = await apiClient.get(`${BASE_URL}/${userId}/details`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch employee details for ${userId}:`, error);
      throw error;
    }
  },

  updateEmployeeDetails: async (userId, data) => {
    try {
      const response = await apiClient.put(`${BASE_URL}/${userId}/details`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update employee details for ${userId}:`, error);
      throw error;
    }
  },

  // === Bank Details ===
  getBankDetails: async (userId) => {
    try {
      const response = await apiClient.get(`${BASE_URL}/${userId}/bank-details`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch bank details for ${userId}:`, error);
      throw error;
    }
  },

  saveBankDetails: async (userId, data) => {
    try {
      const response = await apiClient.post(`${BASE_URL}/${userId}/bank-details`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to save bank details for ${userId}:`, error);
      throw error;
    }
  },

  deleteBankDetails: async (id) => {
    try {
      const response = await apiClient.delete(`${BASE_URL}/bank-details/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to delete bank details ${id}:`, error);
      throw error;
    }
  },

  // === Documents ===
  getDocuments: async (userId) => {
    try {
      const response = await apiClient.get(`${BASE_URL}/${userId}/documents`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch documents for ${userId}:`, error);
      throw error;
    }
  },

  saveDocument: async (userId, data) => {
    try {
      const response = await apiClient.post(`${BASE_URL}/${userId}/documents`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to save document for ${userId}:`, error);
      throw error;
    }
  },

  deleteDocument: async (id) => {
    try {
      const response = await apiClient.delete(`${BASE_URL}/documents/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to delete document ${id}:`, error);
      throw error;
    }
  },

  verifyDocument: async (id) => {
    try {
      const response = await apiClient.post(`${BASE_URL}/documents/${id}/verify`);
      return response.data;
    } catch (error) {
      console.error(`Failed to verify document ${id}:`, error);
      throw error;
    }
  },

  // === Payroll Info ===
  getPayrollInfo: async (userId) => {
    try {
      const response = await apiClient.get(`${BASE_URL}/${userId}/payroll`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch payroll info for ${userId}:`, error);
      throw error;
    }
  },

  savePayrollInfo: async (userId, data) => {
    try {
      const response = await apiClient.post(`${BASE_URL}/${userId}/payroll`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to save payroll info for ${userId}:`, error);
      throw error;
    }
  },

  // === Shift Preferences ===
  getShiftPreferences: async (userId) => {
    try {
      const response = await apiClient.get(`${BASE_URL}/${userId}/shift-preferences`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch shift preferences for ${userId}:`, error);
      throw error;
    }
  },

  saveShiftPreferences: async (userId, data) => {
    try {
      const response = await apiClient.post(`${BASE_URL}/${userId}/shift-preferences`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to save shift preferences for ${userId}:`, error);
      throw error;
    }
  },

  // === Leave Balance ===
  getLeaveBalances: async (userId, year = null) => {
    try {
      const url = year
        ? `${BASE_URL}/${userId}/leave-balance?year=${year}`
        : `${BASE_URL}/${userId}/leave-balance`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch leave balances for ${userId}:`, error);
      throw error;
    }
  },

  saveLeaveBalance: async (userId, data) => {
    try {
      const response = await apiClient.post(`${BASE_URL}/${userId}/leave-balance`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to save leave balance for ${userId}:`, error);
      throw error;
    }
  },

  // === Assigned Stations ===
  getAssignedStations: async (userId) => {
    try {
      const response = await apiClient.get(`${BASE_URL}/${userId}/assigned-stations`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch assigned stations for ${userId}:`, error);
      throw error;
    }
  },

  assignStation: async (userId, data) => {
    try {
      const response = await apiClient.post(`${BASE_URL}/${userId}/assigned-stations`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to assign station for ${userId}:`, error);
      throw error;
    }
  },

  unassignStation: async (userId, stationId) => {
    try {
      const response = await apiClient.delete(`${BASE_URL}/${userId}/assigned-stations/${stationId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to unassign station ${stationId} for ${userId}:`, error);
      throw error;
    }
  },

  // === Reference Data ===
  getReferenceData: async () => {
    try {
      const response = await apiClient.get(`${BASE_URL}/reference-data`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch employee reference data:', error);
      throw error;
    }
  },
};

export default employeeService;
